import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GradFlow } from "gradflow";

import Post from "../components/Post";
import Footer from "../components/Footer";
import { HERO_GRADFLOW_CONFIG } from "../utils/heroGradient";
import { useNavbarHeight } from "../utils/useNavbarHeight";
import { useAccount } from "../utils/useAccount";
import { statusMeta } from "../utils/post";

/* Status filters for the reporter's own feed, in pipeline order. The keys are
   `statusMeta().key`, so they cannot drift from what the post page shows. */
const FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "ongoing", label: "In review" },
  { key: "resolved", label: "Resolved" },
];

const SKELETON_COUNT = 8;

/**
 * /me - who you are, and the reports you have filed.
 *
 * This replaces /myprofile and /mybleeps, which were two pages carrying the same
 * header, the same avatar fetch and the same identity block. Staff accounts
 * cannot post, so for them the feed is not rendered at all (rather than rendered
 * empty), and the page is the identity band alone.
 *
 * The account details live on /me/settings, reached from the band. They are a
 * rare, one-field-at-a-time errand, and sitting above the feed they were a slab
 * to scroll past on every visit.
 */
const Me = ({ domain, userData, refreshUserData }) => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  useNavbarHeight();

  const {
    userId,
    isStaff,
    isReporter,
    displayName,
    email,
    isAnonymous,
    joined,
    hasPhoto,
    avatarSrc,
  } = useAccount(userData, refreshUserData);

  /* -------------------------------------------------------------------- Posts */
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!userId || !isReporter) {
      setPostsLoading(false);
      return;
    }
    let cancelled = false;
    setPostsLoading(true);

    fetch(`/api/posts/userbleeps?user=${encodeURIComponent(userId)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setPosts(Array.isArray(data) ? data : []);
        setPostsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching bleeps:", err);
        setPostsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, isReporter]);

  const counts = useMemo(() => {
    const tally = { all: posts.length, pending: 0, ongoing: 0, resolved: 0 };
    posts.forEach((post) => {
      const key = statusMeta(post.status)?.key;
      if (key) tally[key] += 1;
    });
    return tally;
  }, [posts]);

  const visiblePosts = useMemo(
    () =>
      filter === "all"
        ? posts
        : posts.filter((post) => statusMeta(post.status)?.key === filter),
    [posts, filter],
  );

  /* /mybleeps redirects to /me#bleeps, so a bookmark of the old page still
     lands on the feed rather than at the top of the page. React Router only
     swaps the tree, so nothing scrolls on its own; ScrollToTop deliberately
     leaves a hashed location alone for this to handle. */
  useEffect(() => {
    if (hash !== "#bleeps" || !isReporter) return;
    document.getElementById("bleeps")?.scrollIntoView({ block: "start" });
  }, [hash, isReporter]);

  /* Home, not navigate(-1). This page is reachable from the navbar dropdown on
     every page, so "back" could be anywhere -- or nowhere, if /me was opened
     first in the session. Staff go to the console instead, since "/" bounces
     them to /dashboard anyway and this saves the round trip. */
  const leave = () => navigate(isStaff ? "/dashboard" : "/");

  return (
    <div className="bleep-light bl-page">
      <Helmet>
        <title>My Account | BLEEP</title>
        <meta
          name="description"
          content="Your BLEEP account and the reports you have filed."
        />
      </Helmet>

      {/* ------------------------------------------------------------- Identity */}
      <section className="bl-hero me-hero">
        <GradFlow className="bl-hero__gradflow" config={HERO_GRADFLOW_CONFIG} />

        <div className="bl-shell me-hero__inner">
          <div className="bl-head-row me-hero__bar">
            <div className="me-hero__back">
              <button
                type="button"
                className="bl-close-btn"
                onClick={leave}
                aria-label={
                  isStaff ? "Back to the dashboard" : "Back to the homepage"
                }
              >
                <i
                  className="fas fa-chevron-left"
                  aria-hidden="true"
                />
              </button>
              <span className="bl-eyebrow">My account</span>
            </div>

            {/* Here rather than in the navbar: this is the one thing on the page
                that leads somewhere else, and it belongs with the identity it
                edits. Below 576px the label goes and the cog stands alone, which
                is why the name is on the element rather than only in the text. */}
            <Link
              to="/me/settings"
              className="me-hero__action"
              aria-label="Account settings"
            >
              <i
                className="fa-solid fa-gear"
                aria-hidden="true"
              />
              <span className="me-hero__action-label">Account settings</span>
            </Link>
          </div>

          <div className="me-id">
            <span
              className={`me-id__avatar${hasPhoto ? " me-id__avatar--photo" : ""}`}
            >
              <img
                src={avatarSrc}
                alt=""
              />
            </span>

            <div className="me-id__text">
              <h1 className="me-id__name">{displayName}</h1>
              {email && <p className="me-id__email">{email}</p>}

              <div className="me-tags">
                {isAnonymous && (
                  <span className="me-tag">
                    <i
                      className="fa-solid fa-user-secret"
                      aria-hidden="true"
                    />
                    Anonymous
                  </span>
                )}
                {joined && (
                  <span className="me-tag">
                    <i
                      className="fa-regular fa-calendar"
                      aria-hidden="true"
                    />
                    Joined {joined}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ My bleeps */}
      {isReporter && (
        <section
          id="bleeps"
          className="bl-section bl-section--dark me-bleeps"
        >
          <div className="bl-shell">
            <div className="bl-section-head">
              <div>
                <h2 className="bl-section-title">My bleeps</h2>
                <p className="bl-section-lead">
                  Every report you have filed, newest first.
                </p>
              </div>
            </div>

            {/* Pressed rather than checked: these are buttons that change what
                the grid below shows, not a form the page submits. */}
            {posts.length > 0 && (
              <div
                className="me-seg"
                role="group"
                aria-label="Filter your bleeps by status"
              >
                {FILTERS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className="me-seg__btn"
                    aria-pressed={filter === option.key}
                    onClick={() => setFilter(option.key)}
                  >
                    {option.label}
                    <span className="me-seg__count">{counts[option.key]}</span>
                  </button>
                ))}
              </div>
            )}

            {postsLoading ? (
              <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
                {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <div
                    className="col pb-3"
                    key={index}
                  >
                    <div className="bleep-post-skeleton bl-pulse">
                      <div className="bleep-post-skeleton__media" />
                      <div className="bleep-post-skeleton__body">
                        <div className="bleep-post-skeleton__line" />
                        <div
                          className="bleep-post-skeleton__line"
                          style={{ width: "80%" }}
                        />
                        <div
                          className="bleep-post-skeleton__line"
                          style={{ width: "45%" }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : visiblePosts.length > 0 ? (
              <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
                {visiblePosts.map((post) => (
                  <Post
                    key={post._id}
                    post={post}
                  />
                ))}
              </div>
            ) : posts.length > 0 ? (
              /* Filtered to nothing, which is a different state from having
                 filed nothing: the way out is the filter, not the form. */
              <div className="bl-empty">
                <div
                  className="bl-empty__icon"
                  aria-hidden="true"
                >
                  <i className="fas fa-filter" />
                </div>
                <h3 className="bl-empty__title">Nothing in this status</h3>
                <p className="bl-empty__text">
                  None of your bleeps are{" "}
                  {FILTERS.find((f) => f.key === filter)?.label.toLowerCase()}{" "}
                  right now.
                </p>
                <button
                  type="button"
                  className="me-btn me-empty-cta"
                  onClick={() => setFilter("all")}
                >
                  Show all bleeps
                </button>
              </div>
            ) : (
              <div className="bl-empty">
                <div
                  className="bl-empty__icon"
                  aria-hidden="true"
                >
                  <i className="fas fa-bolt" />
                </div>
                <h3 className="bl-empty__title">No bleeps yet</h3>
                <p className="bl-empty__text">
                  Spotted something fake or substandard? File a report and we
                  will take it from there.
                </p>
                <button
                  type="button"
                  className="bleep-btn-report bleep-btn-report--sm me-empty-cta"
                  onClick={() => navigate("/report")}
                >
                  <i
                    className="fas fa-bolt"
                    aria-hidden="true"
                  />
                  Report a product
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      <Footer domain={domain} />
    </div>
  );
};

export default Me;
